import React from 'react';
import { Drawer, Box, IconButton } from '@mui/material';
import { tablet, hexA, money, orderCode, minutesSince } from './theme';
import { phaseOf, PHASE_BY_KEY, phaseHint, type Phase } from './phases';
import { useTicker } from './useTicker';
import type { AdminOrder } from './types';

function custName(o: AdminOrder) {
  return o.user ? `${o.user.firstName || ''} ${o.user.lastName || ''}`.trim() || 'Customer' : 'Customer';
}
function isDelivery(o: AdminOrder) {
  return !!o.shippingAddressId || !!o.shippingNote || parseFloat(o.totalDeliveryFee) > 0;
}

/**
 * Street line then area line, skipping whatever the address doesn't have.
 * Returns null when there's nothing worth printing, so the caller can hide the
 * whole block rather than render an empty card.
 */
function addressLines(o: AdminOrder): string[] | null {
  const a = o.shippingAddress;
  if (!a) return null;

  const street = [a.buildingName, [a.streetNumber, a.streetName].filter(Boolean).join(' ')]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(', ');
  const area = [a.suburb, a.city, a.postalCode].map((p) => (p || '').trim()).filter(Boolean).join(', ');

  const lines = [street, area].filter(Boolean);
  return lines.length ? lines : null;
}

/** Order lifecycle, in the order it actually happens. */
const TIMELINE: Array<{ key: keyof AdminOrder; label: string }> = [
  { key: 'createdAt', label: 'Order placed' },
  { key: 'pickingStartedAt', label: 'Accepted · picking' },
  { key: 'packedAt', label: 'Packed · ready' },
  { key: 'dispatchedAt', label: 'Collected · en route' },
  { key: 'deliveredAt', label: 'Delivered' },
];

const formatStamp = (iso: string) =>
  new Date(iso).toLocaleString('en-ZA', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });

/**
 * Read-only order detail. Deliberately has no Accept / Mark packed buttons:
 * Live Orders is an ops view, and every transition belongs to the shop tablet.
 */
export function OrderDrawer({ order, onClose }: {
  order: AdminOrder | null;
  onClose: () => void;
}) {
  const open = !!order;
  const phase: Phase = order ? phaseOf(order.status) : 'new';
  const def = PHASE_BY_KEY[phase];
  // The header chip shows an age, which would otherwise freeze at whatever it
  // was when the drawer opened.
  useTicker();
  const address = order ? addressLines(order) : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 430, maxWidth: '94vw', bgcolor: tablet.bg } }}
    >
      {order && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box sx={{ p: 2.5, borderBottom: `1px solid ${tablet.line}` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ fontSize: 20, fontWeight: 800, color: tablet.ink }}>
                {custName(order)}
                <span style={{ color: tablet.faint, fontWeight: 500, fontSize: 15 }}>
                  {' '}{orderCode(order.number, order.id)}
                </span>
              </Box>
              <IconButton onClick={onClose} size="small" sx={{ borderRadius: '10px', bgcolor: tablet.surface }}>✕</IconButton>
            </Box>

            <Box
              sx={{
                mt: 1.25, display: 'inline-flex', alignItems: 'center', gap: 0.75,
                px: 1.25, py: 0.75, borderRadius: `${tablet.radiusSm}px`,
                bgcolor: hexA(def.pin, 0.12), color: def.pin, fontWeight: 800,
              }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: def.pin }} />
              {def.label}
              <span style={{ color: tablet.muted, fontWeight: 600, marginLeft: 8 }}>{phaseHint(phase)}</span>
            </Box>

            <Box sx={{ mt: 1.25, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              <Chip>{order.store?.name || order.storeId}</Chip>
              <Chip>{isDelivery(order) ? 'Delivery' : 'Collection'}</Chip>
              <Chip>{minutesSince(order.createdAt)} min ago</Chip>
              {order.user?.phoneNumber && <Chip>{order.user.phoneNumber}</Chip>}
            </Box>
          </Box>

          {/* Body */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
            {/* Where it's going — the first thing ops reaches for on a delivery. */}
            {isDelivery(order) && address && (
              <>
                <SectionLabel>Deliver to</SectionLabel>
                <Box
                  sx={{
                    mb: 1.75, p: 1.75,
                    bgcolor: tablet.card,
                    border: `1px solid ${tablet.line}`,
                    borderRadius: `${tablet.radius}px`,
                  }}
                >
                  {address.map((line, i) => (
                    <Box key={line} sx={{ color: i === 0 ? tablet.ink : tablet.muted, fontWeight: i === 0 ? 700 : 500, fontSize: 14 }}>
                      {line}
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {order.shippingNote && (
              <Box
                sx={{
                  mb: 1.75, p: 1.5, borderRadius: `${tablet.radiusSm}px`,
                  bgcolor: hexA(tablet.orange, 0.1), color: tablet.ink, fontSize: 14,
                }}
              >
                “{order.shippingNote}”
              </Box>
            )}

            <SectionLabel>Items</SectionLabel>
            <Box sx={{ bgcolor: tablet.card, border: `1px solid ${tablet.line}`, borderRadius: `${tablet.radius}px`, p: 1.75, mb: 1.75 }}>
              {(order.lines || []).length === 0 ? (
                <Box sx={{ color: tablet.faint, fontSize: 14 }}>No line items on this order.</Box>
              ) : (
                (order.lines || []).map((l) => (
                  <Box key={l.id} sx={{ display: 'flex', alignItems: 'center', py: 0.75 }}>
                    <Box sx={{ width: 30, fontWeight: 800, color: tablet.ink }}>{Number(l.quantity) || 1}</Box>
                    <Box sx={{ flex: 1, color: tablet.ink }}>{l.productName}</Box>
                    <Box sx={{ fontWeight: 700, color: tablet.ink }}>
                      {money((Number(l.quantity) || 1) * (parseFloat(l.price) || 0))}
                    </Box>
                  </Box>
                ))
              )}
            </Box>

            <SectionLabel>Transaction</SectionLabel>
            <Box sx={{ bgcolor: tablet.surface, borderRadius: `${tablet.radius}px`, p: 1.75, mb: 1.75 }}>
              <Row label="Subtotal" value={money(parseFloat(order.totalCart) || 0)} />
              <Row
                label={isDelivery(order) ? 'Delivery fee' : 'Collection'}
                value={parseFloat(order.totalDeliveryFee) ? money(parseFloat(order.totalDeliveryFee)) : 'Free'}
              />
              {parseFloat(order.totalServiceFee) > 0 && (
                <Row label="Service fee" value={money(parseFloat(order.totalServiceFee))} />
              )}
              {parseFloat(order.totalDriverTip) > 0 && (
                <Row label="Driver tip" value={money(parseFloat(order.totalDriverTip))} />
              )}
              {parseFloat(order.totalPromo) > 0 && (
                <Row label="Promo" value={`− ${money(parseFloat(order.totalPromo))}`} />
              )}
              <Box sx={{ height: '1px', bgcolor: tablet.line, my: 1 }} />
              <Row label="Total" value={money(parseFloat(order.total) || 0)} bold />
            </Box>

            <SectionLabel>Timeline</SectionLabel>
            <Box sx={{ bgcolor: tablet.card, border: `1px solid ${tablet.line}`, borderRadius: `${tablet.radius}px`, p: 1.75 }}>
              {TIMELINE.map((step) => {
                const stamp = order[step.key] as string | null | undefined;
                const done = !!stamp;
                return (
                  <Box key={String(step.key)} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.6 }}>
                    <Box
                      sx={{
                        width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                        bgcolor: done ? def.pin : 'transparent',
                        border: done ? 'none' : `2px solid ${tablet.line}`,
                      }}
                    />
                    <Box sx={{ flex: 1, color: done ? tablet.ink : tablet.faint, fontWeight: done ? 700 : 500, fontSize: 14 }}>
                      {step.label}
                    </Box>
                    <Box sx={{ color: tablet.faint, fontSize: 12 }}>
                      {done ? formatStamp(stamp as string) : 'Pending'}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Footer — read-only by design */}
          <Box sx={{ p: 2, borderTop: `1px solid ${tablet.line}` }}>
            <Box
              sx={{
                textAlign: 'center', py: 1.5, borderRadius: '13px',
                bgcolor: tablet.surface, color: tablet.muted, fontWeight: 700, fontSize: 13,
              }}
            >
              View only · manage this order from the shop tablet
            </Box>
          </Box>
        </Box>
      )}
    </Drawer>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: tablet.faint, mb: 0.75 }}>
      {children}
    </Box>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ px: 1, py: 0.5, borderRadius: '9px', bgcolor: tablet.surface, color: tablet.muted, fontSize: 12, fontWeight: 600 }}>
      {children}
    </Box>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
      <Box sx={{ color: bold ? tablet.ink : tablet.muted, fontWeight: bold ? 800 : 500 }}>{label}</Box>
      <Box sx={{ color: tablet.ink, fontWeight: bold ? 800 : 700 }}>{value}</Box>
    </Box>
  );
}
